import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { embedOne } from '../../lib/utils';
import { Spinner, SystemEvent } from '../../components/design-system';
import { HandbookView } from '../../components/handbook/HandbookView';
import type { HandbookContent } from '../../types';

function isHandbook(c: unknown): c is HandbookContent {
  return !!c && typeof c === 'object' && 'days' in (c as object);
}

/**
 * HandbookPreview — "Preview as traveler": renders the host's handbook
 * through the exact same HandbookView the traveler sees, read-only
 * (no approve action, no "ask host" — the host IS the host).
 */
export function HandbookPreview() {
  const { matchId } = useParams<{ matchId: string }>();
  const { user, profile } = useAuth();
  const [handbook, setHandbook] = useState<HandbookContent | null>(null);
  const [travelerFirst, setTravelerFirst] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !matchId) return;
    void (async () => {
      const { data: match } = await supabase
        .from('matches')
        .select(`
          traveler_profile:profiles!matches_traveler_id_fkey(full_name),
          itinerary:itineraries(content)
        `)
        .eq('id', matchId)
        .eq('host_id', user.id)
        .maybeSingle();

      if (match) {
        const raw = match as Record<string, unknown>;
        const tp = raw.traveler_profile as { full_name: string | null } | null;
        setTravelerFirst(tp?.full_name?.split(' ')[0] ?? null);
        const content = embedOne(raw.itinerary as { content: unknown } | { content: unknown }[] | null)?.content;
        if (isHandbook(content)) setHandbook(content);
      }
      setLoading(false);
    })();
  }, [user, matchId]);

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  const hostFirstName = profile?.full_name?.split(' ')[0] ?? 'Your host';

  if (!handbook || handbook.days.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center gap-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
          <i className="ti ti-eye text-[26px]" style={{ color: 'var(--accent)' }} />
        </div>
        <div>
          <h2 className="font-serif text-2xl text-app-fg mb-1">Nothing to preview yet</h2>
          <p className="text-sm text-app-muted">Add days and events in the builder, then check back here.</p>
        </div>
        <Link to={`/host/itinerary/${matchId}`} className="text-sm font-medium underline underline-offset-2" style={{ color: 'var(--accent)' }}>
          Open the builder
        </Link>
      </div>
    );
  }

  return (
    <HandbookView
      handbook={handbook}
      hostFirstName={hostFirstName}
      hostAvatarUrl={profile?.avatar_url}
      openOnToday
      headerTop={
        <div className="flex justify-center mb-6">
          <SystemEvent>
            <i className="ti ti-eye" style={{ fontSize: 11, marginRight: 5 }} />
            Previewing exactly as {travelerFirst ?? 'your traveler'} will see it
          </SystemEvent>
        </div>
      }
    />
  );
}

export default HandbookPreview;
